import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "backdrop", "badge", "badgeCount", "inbox", "audio"]
  static classes = ["open", "closed"]
  static values  = { currentRole: String, chatRoomId: Number } // "admin" or "guest"

  connect() {
    this.ensurePortal()
    // seed unread from data-unread-count (no hard-coded 0)
    this.unread = 0
    if (this.hasBadgeTarget) {
      const initial = parseInt(this.badgeTarget.dataset.unreadCount || "0", 10)
      if (!Number.isNaN(initial)) this.unread = initial
    }
    this.updateBadge()

    this._bindKeydown = (e) => { if (e.key === "Escape") this.close() }

    // Observe the inbox (works for per-room and the global admin inbox)
    if (this.hasInboxTarget) {
      const m = this.inboxTarget.id && this.inboxTarget.id.match(/chat_room_(\d+)_notify/)
      if (m) this.chatRoomIdValue = parseInt(m[1], 10)

      this.observer = new MutationObserver((mutations) => this.handleMutations(mutations))
      // Observe direct children; Turbo appends the node into this target
      this.observer.observe(this.inboxTarget, { childList: true })
    }

    // Guests only: report page context + listen for nav changes
    if ((this.currentRoleValue || "guest") !== "admin") {
      this.reportContext()
      this._reportContextListener = () => this.reportContext()
      window.addEventListener("popstate", this._reportContextListener)
      document.addEventListener("turbo:load", this._reportContextListener)
    }

    // External triggers (e.g. Talk to us button)
    this._externalOpenListener = () => this.open()
    this._externalCloseListener = () => this.close()
    this._externalToggleListener = () => this.toggle()
    window.addEventListener("chat-widget:open", this._externalOpenListener)
    window.addEventListener("chat-widget:close", this._externalCloseListener)
    window.addEventListener("chat-widget:toggle", this._externalToggleListener)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this._reportContextListener) {
      window.removeEventListener("popstate", this._reportContextListener)
      document.removeEventListener("turbo:load", this._reportContextListener)
    }
    if (this.hasPanelTarget) this.close()
    window.removeEventListener("chat-widget:open", this._externalOpenListener)
    window.removeEventListener("chat-widget:close", this._externalCloseListener)
    window.removeEventListener("chat-widget:toggle", this._externalToggleListener)
  }

  open(e) {
    e?.preventDefault()
    if (!this.hasPanelTarget || !this.hasBackdropTarget) return
    this.show(this.panelTarget)
    this.show(this.backdropTarget)
    document.addEventListener("keydown", this._bindKeydown)
    this.unread = 0
    this.updateBadge()
  }

  close() {
    this.hide(this.panelTarget)
    this.hide(this.backdropTarget)
    document.removeEventListener("keydown", this._bindKeydown)
  }

  toggle() {
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  // --- helpers ---
  show(el) { el.classList.remove(...(this.closedClasses || ["hidden"])); el.classList.add(...(this.openClasses || ["!block"])) }
  hide(el) { el.classList.remove(...(this.openClasses  || ["!block"])); el.classList.add(...(this.closedClasses || ["hidden"])) }
  isOpen() { return !this.panelTarget.classList.contains((this.closedClasses || ["hidden"])[0]) }

  handleMutations(mutations) {
    for (const m of mutations) {
      m.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return
        if (!node.classList.contains("chat-notify")) return

        const authorRaw = (node.dataset.author || "").toLowerCase()
        const authorIsAdmin = ["admin", "adminuser", "admin_user", "staff"].includes(authorRaw)
        const myRole = (this.currentRoleValue || "guest").toLowerCase()
        const isFromOtherSide = (myRole === "admin") ? !authorIsAdmin : authorIsAdmin

        if (isFromOtherSide) {
          // If a precise count is provided, use it; otherwise increment.
          const provided = parseInt(node.dataset.badgeCount || "NaN", 10)
          if (!Number.isNaN(provided)) {
            this.unread = provided
          } else if (!this.isOpen() || document.hidden) {
            this.unread++
          }
          this.updateBadge()

          // play sound when not focused/open (or always – your call)
          this.playSound()
        }

        node.remove()
      })
    }
  }

  updateBadge() {
    if (!this.hasBadgeTarget) return
    if (this.unread > 0) {
      if (this.hasBadgeCountTarget) {
        this.badgeCountTarget.textContent = String(this.unread)
      } else {
        this.badgeTarget.textContent = String(this.unread)
      }
      this.badgeTarget.classList.remove("hidden")
      this.badgeTarget.dataset.unreadCount = String(this.unread)
    } else {
      this.badgeTarget.classList.add("hidden")
      if (this.hasBadgeCountTarget) this.badgeCountTarget.textContent = "0"
      this.badgeTarget.dataset.unreadCount = "0"
    }
  }

  playSound() {
    if (!this.hasAudioTarget) return
    try {
      this.audioTarget.currentTime = 0
      this.audioTarget.play()
    } catch (_) {}
  }

  // --- page context reporting (support namespace; guests only) ---
  reportContext() {
    if ((this.currentRoleValue || "guest") === "admin") return
    if (!this.chatRoomIdValue) return
    try {
      const body = new FormData()
      body.append("title", document.title || "")
      body.append("url",   window.location.href || "")
      fetch(`/support/chat_rooms/${this.chatRoomIdValue}/context`, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken() },
        body
      })
    } catch (_) {}
  }

  csrfToken() {
    const el = document.querySelector("meta[name='csrf-token']")
    return el && el.content
  }

  // Move the widget wrapper to <body> so it isn't constrained by layout wrappers.
  ensurePortal() {
    if (typeof document === "undefined") return

    const portal = document.getElementById("chat-widget-portal")
    const target = portal || document.body

    if (this.element.parentElement === target) return

    try {
      target.appendChild(this.element)
    } catch (_) {}
  }
}
